// Servisfy Status & Updates — Business Web API client (the sole all_registered_businesses feature).
// Business reads are authenticated-Business-open (no tenant entitlement). The feed FAILS OPEN: any error
// resolves to an empty, non-blocking result so a status outage never blocks login/CRM/calls/navigation.
//
// Two AUTH PLANES, intentionally separate:
//  • Business read plane (fetchStatusUpdates / unread / mark-seen) → Firebase Business token (apiGet/apiPost).
//  • SuperAdmin authoring plane (statusAdmin.*) → the layered SuperAdmin session (saGet/saPost). The owner
//    routes require require_superadmin_session; the Firebase token is null in the SuperAdmin portal, which
//    is exactly why routing statusAdmin through apiGet/apiPost produced 401 on every authoring call.
import { apiGet, apiPost } from './apiClient';
import { saGet, saPost } from './superAdminApiClient';

const BIZ = '/api/v1/status';
const ADMIN = '/api/v1/super-admin/status';

// Explicit feed states — a status outage or an auth failure must NEVER look like an empty (healthy) feed,
// and an authenticated-empty feed must never claim "all systems healthy". (Phase 2F.)
export const FEED_STATE = {
  LOADING: 'LOADING',
  UNAUTHORIZED: 'UNAUTHORIZED',        // 401 — session missing/expired
  FORBIDDEN: 'FORBIDDEN',              // 403 — authenticated but not permitted
  BACKEND_ERROR: 'BACKEND_ERROR',      // 5xx / network — served nothing
  PARTIAL_CONTENT: 'PARTIAL_CONTENT',  // served, but the backend flagged the feed degraded/incomplete
  ACTIVE_CONTENT: 'ACTIVE_CONTENT',    // published notices present
  AUTHENTICATED_EMPTY: 'AUTHENTICATED_EMPTY', // authenticated, no applicable notices (NOT proof of health)
};

// Classify a thrown feed error into a discriminated status. apiGet throws Error(`${status} ${statusText}`),
// so the leading integer is the HTTP status; a network failure (no leading status) → backend_error.
export function classifyFeedError(err) {
  const m = /^\s*(\d{3})\b/.exec((err && err.message) || '');
  const code = m ? Number(m[1]) : 0;
  if (code === 401) return 'unauthorized';
  if (code === 403) return 'forbidden';
  return 'backend_error'; // 5xx, 0/network, and any other non-auth failure
}

// Pure UI-state derivation — single source of truth shared by the component and its tests.
export function deriveFeedState({ loading, status, partial, itemCount } = {}) {
  if (loading) return FEED_STATE.LOADING;
  if (status === 'unauthorized') return FEED_STATE.UNAUTHORIZED;
  if (status === 'forbidden') return FEED_STATE.FORBIDDEN;
  if (status === 'backend_error') return FEED_STATE.BACKEND_ERROR;
  if (partial) return FEED_STATE.PARTIAL_CONTENT;
  if ((itemCount || 0) > 0) return FEED_STATE.ACTIVE_CONTENT;
  return FEED_STATE.AUTHENTICATED_EMPTY;
}

// Operational states a day bucket / the current banner can hold. Mirrors api/status_uptime.py —
// NOT a new severity enum; these are derived from the existing type + severity values.
export const OPS_STATE = {
  OPERATIONAL: 'OPERATIONAL',
  DEGRADED: 'DEGRADED',
  SCHEDULED_MAINTENANCE: 'SCHEDULED_MAINTENANCE',
  OUTAGE: 'OUTAGE',
  UNKNOWN: 'unknown',
};

// Fetch the authoritative current operational state + rolling uptime history.
// FAIL-OPEN, and critically FAIL-UNKNOWN: any error yields ok:false / state 'unknown' so the banner
// renders the honest unavailable treatment. It must NEVER fall back to a green "operational" guess —
// that is the Phase 2F false-healthy protection, expressed on the client side.
export async function fetchStatusUptime({ days = 90 } = {}) {
  try {
    const r = await apiGet(`${BIZ}/uptime?days=${days}&plane=business_web`);
    if (!r || r.ok === false) {
      return { ok: false, state: OPS_STATE.UNKNOWN, days: [], uptimePercent: null, timezone: null };
    }
    return {
      ok: true,
      state: r.current_operational_state || OPS_STATE.UNKNOWN,
      days: Array.isArray(r.days) ? r.days : [],
      uptimePercent: typeof r.uptime_percent === 'number' ? r.uptime_percent : null,
      timezone: r.timezone || null,
      counts: r.counts || {},
      maintenanceSeconds: r.scheduled_maintenance_seconds || 0,
      downtimeSeconds: r.unplanned_downtime_seconds || 0,
      windowStartLocal: r.window_start_local_date || null,
      windowEndLocal: r.window_end_local_date || null,
    };
  } catch (e) {
    return { ok: false, state: OPS_STATE.UNKNOWN, days: [], uptimePercent: null, timezone: null };
  }
}

// ── Business read plane (fail-open) ─────────────────────────────────────────────────────────────────
export async function fetchStatusUpdates({ limit = 20, offset = 0, includeHistory = false } = {}) {
  try {
    const r = await apiGet(
      `${BIZ}/updates?limit=${limit}&offset=${offset}&include_history=${includeHistory ? 'true' : 'false'}&plane=business_web`);
    const partial = !!r?.degraded; // backend served but flagged the feed incomplete
    // degraded retained (fail-open contract): true whenever the feed is not a clean, complete OK.
    return { items: r?.items || [], count: r?.count || 0, status: 'ok', partial, degraded: partial };
  } catch (e) {
    // Fail-open — never throw into the portal — but preserve WHY so the UI can be honest (401≠5xx≠empty).
    return { items: [], count: 0, status: classifyFeedError(e), partial: false, degraded: true };
  }
}

export async function fetchStatusUnreadCount() {
  try {
    const r = await apiGet(`${BIZ}/updates/unread-count`);
    return Number(r?.unread || 0);
  } catch (e) {
    return 0; // fail-open — no dot rather than an error
  }
}

export async function markStatusSeen() {
  try { await apiPost(`${BIZ}/updates/mark-seen`, {}); return true; } catch (e) { return false; }
}

// ── SuperAdmin authoring plane (owner only; layered-session bearer; errors surface to the author) ─────
export const statusAdmin = {
  list: (lifecycle) => saGet(`${ADMIN}/posts${lifecycle ? `?lifecycle=${lifecycle}` : ''}`),
  create: (post) => saPost(`${ADMIN}/posts`, post),
  preview: (id) => saGet(`${ADMIN}/posts/${id}/preview`),
  edit: (id, patch) => saPost(`${ADMIN}/posts/${id}/update`, patch),
  publish: (id) => saPost(`${ADMIN}/posts/${id}/publish`, {}),
  unpublish: (id) => saPost(`${ADMIN}/posts/${id}/unpublish`, {}),
  incidentUpdate: (id, message) => saPost(`${ADMIN}/posts/${id}/incident-update`, { message }),
  maintenanceStart: (id) => saPost(`${ADMIN}/posts/${id}/maintenance-start`, {}),
  maintenanceComplete: (id) => saPost(`${ADMIN}/posts/${id}/maintenance-complete`, {}),
  resolve: (id, message) => saPost(`${ADMIN}/posts/${id}/resolve`, { message }),
  archive: (id) => saPost(`${ADMIN}/posts/${id}/archive`, {}),
  audit: (id) => saGet(`${ADMIN}/posts/${id}/audit`),
};

// ── Local-time formatting (UTC is authoritative; render in the viewer's local tz) ────────────────────
export function toLocalTime(utcIso) {
  if (!utcIso) return '';
  try {
    const d = new Date(utcIso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch (e) { return ''; }
}

export const STATUS_SECTIONS = [
  { key: 'current', label: 'Current Status' },
  { key: 'incidents', label: 'Active Incidents' },
  { key: 'maintenance', label: 'Scheduled Maintenance' },
  { key: 'whats_new', label: "What's New" },
  { key: 'release_notes', label: 'Release Notes' },
  { key: 'history', label: 'History' },
];
